/**
 * Central billing URLs — the ONLY places BeHeard sends a user to buy, upgrade
 * or manage billing (suite contract 1.2). Central owns plans, checkout and the
 * Stripe portal; BeHeard only links to them.
 *
 *   plans    ${CENTRAL}/workspace/<id>/settings/billing?view=plans&app=beheard&return_to=<abs app URL>
 *   manage   ${CENTRAL}/workspace/<id>/settings/billing?return_to=<abs app URL>
 *   credits  ${CENTRAL}/workspace/<id>/settings/credits?return_to=<abs app URL>
 *
 * Never the legacy paths (`/billing`, `/billing/plans`, `/billing/credits`,
 * `/api/billing/portal`, `/v1/billing/*`): Central serves none of them.
 *
 * After payment Central sends the browser back to `return_to` with
 * `?billing=success|cancel` appended; `components/routing/BootReadyEffects`
 * re-fetches switch-context and billing state on that and strips the param.
 *
 * `return_to` is always built from THIS origin (never from user input), so it
 * can only name a BeHeard page; Central re-validates it against its allowlist.
 */

export type CentralBillingView = 'plans' | 'manage' | 'credits';

/** Query params that describe a hand-off in flight and must not be echoed back. */
const TRANSIENT_PARAMS = ['billing', 'workspaceId'] as const;

/** Session flag: a billing page was opened, so the next focus re-reads billing. */
export const BILLING_OPENED_KEY = 'beheard_central_billing_opened';

function currentLocation(): { origin: string; pathname: string; search: string } | null {
  if (typeof window === 'undefined' || !window.location?.origin) return null;
  return window.location;
}

/**
 * The absolute BeHeard URL Central should send the user back to: `path` (or the
 * current page) on this origin, with transient hand-off params removed. Null
 * when there is no browser origin, or `path` would leave this origin.
 */
export function appReturnUrl(path?: string): string | null {
  const loc = currentLocation();
  if (!loc) return null;
  let url: URL;
  try {
    url = new URL(path ?? `${loc.pathname}${loc.search}`, loc.origin);
  } catch {
    return null;
  }
  if (url.origin !== loc.origin) return null;
  // Same origin is not enough: "https://user:pass@<this host>/x" is same-origin
  // and would carry credentials into Central's return_to (A7-INFO).
  if (url.username || url.password) return null;
  for (const p of TRANSIENT_PARAMS) url.searchParams.delete(p);
  url.hash = '';
  return url.toString();
}

export interface CentralBillingUrlInput {
  centralUrl: string;
  workspaceId: string | null | undefined;
  view: CentralBillingView;
  appId: string;
  /** Absolute app URL; omitted when null. */
  returnTo?: string | null;
}

/**
 * The Central billing page for `view`, or null when it cannot be addressed
 * (Central not configured, or no workspace known). A null is the caller's cue
 * to say so — never to fall back to a legacy path.
 */
export function centralBillingUrl(input: CentralBillingUrlInput): string | null {
  const { centralUrl, workspaceId, view, appId, returnTo } = input;
  if (!centralUrl || !workspaceId) return null;
  let url: URL;
  try {
    const page = view === 'credits' ? 'credits' : 'billing';
    url = new URL(`/workspace/${encodeURIComponent(workspaceId)}/settings/${page}`, centralUrl);
  } catch {
    return null;
  }
  // `view` and `app` belong to the PLANS link only; manage and credits carry
  // `return_to` alone (contract 1.2).
  if (view === 'plans') {
    url.searchParams.set('view', 'plans');
    url.searchParams.set('app', appId);
  }
  if (returnTo) url.searchParams.set('return_to', returnTo);
  return url.toString();
}

/** Remember that billing was opened, so the next focus re-reads billing. */
export function markCentralBillingOpened(): void {
  try {
    sessionStorage.setItem(BILLING_OPENED_KEY, String(Date.now()));
  } catch {
    /* storage unavailable: the ?billing= return still re-reads */
  }
}

/** True once per opened hand-off: clears the flag as it reads it. */
export function consumeCentralBillingOpened(): boolean {
  try {
    const was = sessionStorage.getItem(BILLING_OPENED_KEY) !== null;
    if (was) sessionStorage.removeItem(BILLING_OPENED_KEY);
    return was;
  } catch {
    return false;
  }
}

/** Top-level navigation to a Central billing page (the flag is set first). */
export function openCentralBilling(url: string, navigate: (u: string) => void = (u) => window.location.assign(u)): void {
  markCentralBillingOpened();
  navigate(url);
}

/** `?billing=` on arrival: 'success' | 'cancel', or null for anything else. */
export function readBillingReturn(search: string): 'success' | 'cancel' | null {
  const v = new URLSearchParams(search).get('billing');
  return v === 'success' || v === 'cancel' ? v : null;
}

/**
 * Window event announced when the user is back from Central billing — the
 * `?billing=` return or focus after a billing page was opened from this tab
 * (BootReadyEffects). Every screen that shows Central's credit balance re-reads
 * it on this; nothing else does.
 */
export const CENTRAL_BILLING_REFRESH_EVENT = 'beheard:central-billing-refresh';

/** Tell every balance reader to re-read from Central. */
export function announceCentralBillingRefresh(): void {
  try {
    window.dispatchEvent(new Event(CENTRAL_BILLING_REFRESH_EVENT));
  } catch {
    /* no window (tests / SSR): nothing is mounted to refresh */
  }
}

/** Run `onRefresh` on every announced refresh; returns the unsubscribe. */
export function onCentralBillingRefresh(onRefresh: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return () => undefined;
  window.addEventListener(CENTRAL_BILLING_REFRESH_EVENT, onRefresh);
  return () => window.removeEventListener(CENTRAL_BILLING_REFRESH_EVENT, onRefresh);
}
